import { useMusicBeatGrid } from "../lib/musicBeatGrid";
import { timeChoreography } from "../lib/beatSync";
import { PostFormatToggle } from "./PostFormatToggle";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { getStorage, ref } from "firebase/storage";
import { WATERMARK_STORAGE_PATH, WATERMARK_PUBLIC_URL } from "../constants/watermark";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Copy,
  ImageIcon,
  MoreVertical,
  Pencil,
  Send,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  X,
} from "lucide-react";
import type { FocusEvent } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useAnalyticsUiEnabled } from "../hooks/useAnalyticsUiEnabled";
import { usePostAnalytics } from "../hooks/usePostAnalytics";
import { analyticsReconnectNeeded, PostMetricsRow } from "./PostMetricsRow";
import { useStripe } from "../contexts/StripeContext";
import { usePostActions } from "../hooks/usePostActions";
import { formatDate } from "../lib/utils";
import { resolveMusicUrl } from "../lib/musicUrl";
import { isPostEditable } from "../utils/postStatus";
import { PlatformConnection } from "../models/Platform";
import { Post, PostFeedback, isVideoPost } from "../models/Post";
import { db, getSignedUrl } from "../services/Firebase";
import { postConverter } from "../services/converters";
import { ErrorActionButtons } from "./ErrorActionButtons";
import { MediaPlayer } from "./MediaPlayer";
import { PlatformConnectionIcon } from "./PlatformConnectionIcon";
import { PostSource } from "./PostSource";
import { ProcessedMediaDownloader } from "./ProcessedMediaDownloader";
import { StatusBadge } from "./StatusBadge";
import { useUpgradePage } from "../hooks/useUpgradePage";
import { Alert, AlertDescription } from "./ui/alert";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Separator } from "./ui/separator";
import { Textarea } from "./ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { useToast } from "./ui/use-toast";
import { CarouselVideoPreview } from "../remotion/CarouselVideoPreview";
import { SlideData } from "../remotion/compositions/CarouselVideo";
import { VideoChoreography, isDirectedSpec } from "../models/ChoreographySpec";

interface PostViewerProps {
  isOpen: boolean;
  onClose: () => void;
  expandedPost: Post;
  initialIndex?: number;
  connectionsById: Record<string, PlatformConnection>;
  mediaUrls?: Record<string, string>;
  enableLiveUpdates?: boolean;
  readOnly?: boolean;
}

const iconButtonSize = "sm" as const;

// Function to check if we're running on localhost
const isLocalhost = () => {
  return (
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    process.env.NODE_ENV === "development"
  );
};

export function PostViewer({
  isOpen,
  onClose,
  expandedPost,
  initialIndex = 0,
  connectionsById,
  mediaUrls,
  enableLiveUpdates = true,
  readOnly = false,
}: PostViewerProps) {
  const [currentPostData, setCurrentPostData] = useState<Post>(expandedPost);
  const [listenerError, setListenerError] = useState<Error | null>(null);

  // Core hooks must be initialized first
  const { currentUser, activeWorkspaceId } = useAuth();
  const { currentPlan, isFreePlan } = useStripe();
  const { toast } = useToast();
  // Per-publication engagement metrics (server-written post_analytics docs).
  // UI-gated behind the engagement-analytics-ui flag pending Meta reviews;
  // passing null skips the Firestore subscription entirely.
  const analyticsUiEnabled = useAnalyticsUiEnabled();
  const { byConnectionId: analyticsByConnectionId } = usePostAnalytics(
    analyticsUiEnabled ? currentPostData?.id : null
  );

  // PostActions hook
  const { handleEdit, getDeleteDialog, handleDuplicate } = usePostActions();
  const handleEditAndClose = (
    postId: string,
    options?: { openEditor?: boolean; intent?: "publish"; from?: "post_viewer" },
  ) => {
    // The drafts gallery remains mounted behind its editor route so scroll
    // position is preserved. Close this overlay before navigating or Back
    // would reveal the stale viewer instead of the source row.
    onClose();
    handleEdit(postId, options);
  };

  // Other state variables
  const [currentIndex, setCurrentIndex] = useState(() => {
    return Math.min(
      initialIndex,
      (currentPostData?.mediaItems?.length || 1) - 1
    );
  });
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
  });

  const canPreviewFormats = !!currentPostData?.mediaItems?.length &&
    currentPostData.mediaItems.every(item => item.type === "image") &&
    (!!currentPostData.videoChoreography || (!readOnly && !!currentUser));
  const previewToolbarHeight = canPreviewFormats ? 64 : 0;

  // Video mode: show video preview for video posts (not rendition caches)
  const hasVideoChoreography =
    !!currentPostData && isVideoPost(currentPostData);
  const [showVideoMode, setShowVideoMode] = useState(hasVideoChoreography);
  const [resolvedVideoSlides, setResolvedVideoSlides] = useState<SlideData[]>([]);
  const [videoMusicUrl, setVideoMusicUrl] = useState<string | undefined>();

  useEffect(() => {
    setShowVideoMode(hasVideoChoreography);
  }, [hasVideoChoreography, currentPostData?.id, isOpen]);

  const [isPreparingVideo, setIsPreparingVideo] = useState(false);
  const activePostIdRef = useRef(currentPostData?.id);
  activePostIdRef.current = currentPostData?.id;

  const handlePreviewFormatChange = async (video: boolean) => {
    const imageItems = currentPostData.mediaItems.filter(item => item.type === "image");
    const structureHash = imageItems.map(item =>
      (item.metadata?.overlays ?? []).map(overlay => overlay.id).sort().join(",")
    ).join("|");
    // Directed videos stage slides freely, so only the structure hash says they're current
    const cached = currentPostData.videoChoreography;
    const cachedIsFresh = !!cached &&
      (isDirectedSpec(cached) || cached.slides.length === imageItems.length) &&
      currentPostData.videoChoreographyHash === structureHash;
    // Shared viewers cannot refresh a post. Authenticated previews validate the
    // cache against edited slides so preview and publish use the same sequence.
    if (!video || cachedIsFresh || (readOnly && currentPostData.videoChoreography)) {
      setShowVideoMode(video);
      return;
    }
    if (!currentUser || readOnly || isPreparingVideo) return;
    const postId = currentPostData.id;
    setIsPreparingVideo(true);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/carousel-video/choreography`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ postId, target: "rendition" }),
      });
      const data = await response.json();
      if (!response.ok || !data.success || !data.choreography) throw new Error("Video preview unavailable");
      if (activePostIdRef.current !== postId) return;
      setCurrentPostData(current => current.id !== postId ? current : ({
        ...current,
        videoEnabled: isVideoPost(current),
        videoChoreography: data.choreography,
        videoChoreographyHash: data.choreographyHash,
        ...(data.music && { videoMusicPath: data.music.musicPath, videoMusicName: data.music.musicName }),
      }));
      setShowVideoMode(true);
    } catch {
      toast({ variant: "destructive", title: "Couldn't load video preview", description: "Please try again." });
    } finally {
      setIsPreparingVideo(false);
    }
  };

  // Resolve music URL
  useEffect(() => {
    const musicPath = currentPostData?.videoMusicPath;
    if (currentPostData?.videoAudioMuted || !musicPath) { setVideoMusicUrl(undefined); return; }
    let cancelled = false;
    setVideoMusicUrl(undefined);

    if (mediaUrls?.[musicPath]) {
      setVideoMusicUrl(mediaUrls[musicPath]);
      return () => { cancelled = true; };
    }

    resolveMusicUrl(musicPath, currentUser)
      .then((url) => { if (!cancelled) setVideoMusicUrl(url); })
      .catch((error) => {
        console.warn("Failed to resolve post video music URL:", error);
        if (!cancelled) setVideoMusicUrl(undefined);
      });
    return () => { cancelled = true; };
  }, [currentPostData?.videoAudioMuted, currentPostData?.videoMusicPath, currentUser, mediaUrls]);
  const [, setCancelingRetry] = useState<string | null>(null);
  const [currentVideoTime, setCurrentVideoTime] = useState(0);
  const [isDetailsSheetExpandedOnMobile, setIsDetailsSheetExpandedOnMobile] =
    useState(false);
  const { navigateToUpgrade } = useUpgradePage();

  // Feedback related state
  const [feedbackSelection, setFeedbackSelection] = useState<
    "up" | "down" | null
  >(null);
  const [showFeedbackInput, setShowFeedbackInput] = useState(false);
  const [feedbackComment, setFeedbackComment] = useState("");
  const [existingFeedbackId, setExistingFeedbackId] = useState<string | null>(
    null
  );
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false); // Used for both fetching and submitting

  // State to store the initially loaded feedback values for discard functionality
  const [initialFeedbackRating, setInitialFeedbackRating] = useState<
    "up" | "down" | null
  >(null);
  const [initialFeedbackComment, setInitialFeedbackComment] =
    useState<string>("");

  // Swipe gesture support for mobile details sheet
  // Uses refs + native listeners with { passive: false } so we can preventDefault
  const swipeAreaRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef<number | null>(null);

  useEffect(() => {
    const el = swipeAreaRef.current;
    if (!el) return;

    const SWIPE_THRESHOLD = 40;

    const onTouchStart = (e: TouchEvent) => {
      touchStartY.current = e.touches[0].clientY;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (touchStartY.current === null) return;
      // Prevent scroll while swiping on the handle area
      e.preventDefault();
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (touchStartY.current === null) return;
      const deltaY = touchStartY.current - e.changedTouches[0].clientY;
      touchStartY.current = null;
      if (deltaY > SWIPE_THRESHOLD) {
        setIsDetailsSheetExpandedOnMobile(true);
      } else if (deltaY < -SWIPE_THRESHOLD) {
        setIsDetailsSheetExpandedOnMobile(false);
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, []);

  // Preload overlay image URLs when PostViewer opens
  const [overlayImageUrls, setOverlayImageUrls] = useState<Record<string, string>>({});
  const loadedOverlayUrlsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isOpen || !expandedPost?.mediaItems) {
      return;
    }

    let isMounted = true;

    const preloadOverlayUrls = async () => {
      const storage = getStorage();
      const newUrls: Record<string, string> = {};

      // Collect all image overlay filePaths from all media items
      for (const mediaItem of expandedPost.mediaItems) {
        if (!mediaItem) continue;
        const overlays = mediaItem.metadata?.overlays || [];
        for (const overlay of overlays) {
          if (
            overlay.type === "image" &&
            "filePath" in overlay &&
            overlay.filePath &&
            !mediaUrls?.[overlay.filePath] &&
            !loadedOverlayUrlsRef.current.has(overlay.filePath) &&
            !newUrls[overlay.filePath]
          ) {
            try {
              const storageRef = ref(storage, overlay.filePath);
              const url = await getSignedUrl(storageRef.fullPath);
              newUrls[overlay.filePath] = url;
            } catch (error) {
              console.error("Error preloading overlay image URL:", error);
            }
          }
        }
      }

      if (isMounted && Object.keys(newUrls).length > 0) {
        for (const filePath of Object.keys(newUrls)) {
          loadedOverlayUrlsRef.current.add(filePath);
        }
        setOverlayImageUrls((prev) => ({ ...prev, ...newUrls }));
      }
    };

    preloadOverlayUrls();

    return () => {
      isMounted = false;
    };
  }, [isOpen, expandedPost, mediaUrls]);

  // Resolve video slide URLs when in video mode
  useEffect(() => {
    if (!showVideoMode || !currentPostData?.videoChoreography || !isOpen) {
      setResolvedVideoSlides([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const resolved = await Promise.all(
        currentPostData.mediaItems
          .filter((item) => item.type === "image")
          .map(async (item) => {
            let bgUrl: string | undefined;
            try {
              if (item.path) {
                bgUrl = mediaUrls?.[item.path] ?? await getSignedUrl(item.path);
              }
            } catch { /* ignore */ }
            const imageUrls: Record<string, string> = {};
            for (const overlay of item.metadata?.overlays ?? []) {
              if (overlay.type === "image" && "filePath" in overlay && overlay.filePath) {
                try {
                  imageUrls[overlay.filePath] = mediaUrls?.[overlay.filePath] ?? overlayImageUrls[overlay.filePath] ?? await getSignedUrl(overlay.filePath);
                } catch { /* ignore */ }
              }
            }
            // Inject watermark for free-plan users (same as RemotionOverlayRenderer)
            let overlays = item.metadata?.overlays ?? [];
            if (isFreePlan && item.metadata?.generatedWithWatermark) {
              // The SAME PNG the server composites on export (B1 soft brand
              // chip), served from its public URL — preview matches output.
              overlays = [...overlays, {
                id: '__watermark__',
                type: 'image' as const,
                filePath: WATERMARK_STORAGE_PATH,
                position: { x: 39, y: 95 },
                width: 22, opacity: 100, zIndex: 9999,
                objectFit: 'contain' as const,
              }];
              imageUrls[WATERMARK_STORAGE_PATH] = WATERMARK_PUBLIC_URL;
            }
            return {
              overlays,
              backgroundImageUrl: bgUrl,
              backgroundColor: item.metadata?.backgroundColor,
              backgroundFilter: item.metadata?.backgroundFilter,
              dimensions: item.metadata?.dimensions ?? { width: 1080, height: 1350 },
              imageUrls,
              isBackgroundTemplateAsset: item.metadata?.isBackgroundTemplateAsset,
            } as SlideData;
          })
      );
      if (!cancelled) setResolvedVideoSlides(resolved);
    })();
    return () => { cancelled = true; };
  }, [showVideoMode, currentPostData, isOpen, mediaUrls, overlayImageUrls]);

  // Apply speed to choreography for preview
  // Plays as published: scaled by speed, then cut on the music's beat
  const beatGrid = useMusicBeatGrid(currentPostData?.videoMusicPath, !!currentPostData?.videoAudioMuted, currentUser);
  const speedAdjustedChoreography = useMemo((): VideoChoreography | undefined => {
    const choreo = currentPostData?.videoChoreography;
    return choreo ? timeChoreography(choreo, currentPostData?.videoSpeed, beatGrid) : choreo;
  }, [currentPostData?.videoChoreography, currentPostData?.videoSpeed, beatGrid]);

  // Merge incoming mediaUrls with preloaded overlay URLs
  const mergedMediaUrls = useMemo(() => {
    return { ...(mediaUrls || {}), ...overlayImageUrls };
  }, [mediaUrls, overlayImageUrls]);

  useEffect(() => {
    setCurrentPostData(expandedPost);
    setListenerError(null);

    if (!enableLiveUpdates || !isOpen || !expandedPost?.id) {
      return;
    }

    const postRef = doc(db, "posts", expandedPost.id).withConverter(
      postConverter
    );
    const unsubscribe = onSnapshot(
      postRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          setCurrentPostData({
            ...docSnapshot.data(),
            id: docSnapshot.id,
          } as Post);
          setListenerError(null);
        } else {
          console.warn(
            "Post document deleted or does not exist:",
            expandedPost.id
          );
          setListenerError(
            new Error(
              "Post data could not be loaded. It might have been deleted."
            )
          );
        }
      },
      (error) => {
        console.error("Error listening to post updates:", error);
        setListenerError(error);
      }
    );

    return () => unsubscribe();
  }, [isOpen, expandedPost, enableLiveUpdates]);

  const currentItem = currentPostData?.mediaItems?.[currentIndex];

  // Poster-only approach: always start player timeline at 0
  // (Cover is shown by the player's poster logic; we don't seek here.)
  useEffect(() => {
    setCurrentVideoTime(0);
  }, [currentItem]);

  useEffect(() => {
    if (
      currentPostData?.mediaItems &&
      currentIndex >= currentPostData.mediaItems.length
    ) {
      setCurrentIndex(Math.max(0, currentPostData.mediaItems.length - 1));
    }
  }, [currentPostData?.mediaItems, currentIndex]);

  const hasPublished = useMemo(
    () =>
      Object.values(currentPostData?.connections || {}).some(
        (conn) => conn.status === "published"
      ),
    [currentPostData?.connections]
  );

  // Publishing is the next step for a draft only, as on the Drafts card;
  // scheduled and failed posts are changed through Edit.
  const canPublish = currentPostData?.status === "draft";
  const isEditable = useMemo(
    () => (currentPostData ? isPostEditable(currentPostData) : false),
    [currentPostData]
  );

  useEffect(() => {
    const calculateDimensions = () => {
      const mediaWidth = currentItem?.metadata?.dimensions?.width ?? 1080;
      const mediaHeight = currentItem?.metadata?.dimensions?.height ?? 1350;
      // Match the active preview: generated videos use a 9:16 composition.
      const aspectRatio = showVideoMode ? 9 / 16 : mediaWidth / mediaHeight;

      let calculatedWidth: number;
      let calculatedHeight: number;

      const isMobile = window.innerWidth < 768;

      if (isMobile) {
        // Mobile: Full-screen viewer. Media fills space above details sheet peek.
        const containerWidth = window.innerWidth;
        const detailsPeekHeight = 96; // 6rem details sheet peek
        const availableHeight = window.innerHeight - detailsPeekHeight - previewToolbarHeight;

        calculatedWidth = containerWidth;
        calculatedHeight = Math.min(
          calculatedWidth / aspectRatio,
          availableHeight
        );
      } else {
        // Desktop: Fit within 70% window width and 85% window height.
        const maxWidth = Math.min(window.innerWidth * 0.7, window.innerWidth - 384 - 48);
        const maxHeight = window.innerHeight * 0.85 - previewToolbarHeight;

        // Calculate width and height based on fitting within maxWidth and maxHeight
        // If video is relatively wider than the allowed box aspect ratio
        if (aspectRatio > maxWidth / maxHeight) {
          calculatedWidth = maxWidth;
          calculatedHeight = calculatedWidth / aspectRatio;
        } else {
          // Video is relatively taller (or same aspect ratio) than the allowed box
          calculatedHeight = maxHeight;
          calculatedWidth = calculatedHeight * aspectRatio;
        }
      }
      setDimensions({
        width: Math.round(calculatedWidth),
        height: Math.round(calculatedHeight),
      });
    };

    if (isOpen) {
      calculateDimensions();
      window.addEventListener("resize", calculateDimensions);
      return () => window.removeEventListener("resize", calculateDimensions);
    }
  }, [currentItem, isOpen, previewToolbarHeight, showVideoMode]);

  // Effect to load existing feedback for the current post and user
  useEffect(() => {
    if (!isOpen || !currentPostData?.id || !activeWorkspaceId || !currentUser) {
      setFeedbackSelection(null);
      setFeedbackComment("");
      setShowFeedbackInput(false);
      setExistingFeedbackId(null);
      // Reset initial values for discard
      setInitialFeedbackRating(null);
      setInitialFeedbackComment("");
      return;
    }

    const fetchFeedback = async () => {
      setIsSubmittingFeedback(true);
      try {
        const feedbackCollectionRef = collection(db, "post_feedbacks");
        const q = query(
          feedbackCollectionRef,
          where("workspaceId", "==", activeWorkspaceId),
          where("postId", "==", currentPostData.id),
          where("userId", "==", currentUser.uid)
        );
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const feedbackDoc = querySnapshot.docs[0];
          const feedbackData = feedbackDoc.data() as PostFeedback;
          setFeedbackSelection(feedbackData.rating);
          setFeedbackComment(feedbackData.comment || "");
          setExistingFeedbackId(feedbackDoc.id);
          // Store initial values for discard
          setInitialFeedbackRating(feedbackData.rating);
          setInitialFeedbackComment(feedbackData.comment || "");
        } else {
          setFeedbackSelection(null);
          setFeedbackComment("");
          setShowFeedbackInput(false);
          setExistingFeedbackId(null);
          // Reset initial values for discard
          setInitialFeedbackRating(null);
          setInitialFeedbackComment("");
        }
      } catch (error) {
        console.error("Error fetching feedback:", error);
        toast({
          title: "Error loading feedback",
          description:
            error instanceof Error
              ? error.message
              : "Could not load existing feedback.",
          variant: "destructive",
        });
        setFeedbackSelection(null);
        setFeedbackComment("");
        setShowFeedbackInput(false);
        setExistingFeedbackId(null);
        // Reset initial values on error too
        setInitialFeedbackRating(null);
        setInitialFeedbackComment("");
      } finally {
        setIsSubmittingFeedback(false);
      }
    };

    fetchFeedback();
  }, [isOpen, currentPostData?.id, activeWorkspaceId, currentUser, toast]);

  const handleCancelRetry = async (postId: string, connectionId: string) => {
    if (!currentUser) return;

    try {
      setCancelingRetry(connectionId);

      // 1. Delete the task via API
      const idToken = await currentUser.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE}/tasks/${postId}/${connectionId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );

      if (!response.ok && response.status !== 404) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to cancel retry");
      }

      // 2. Update the connection's error object in Firestore to mark it as cancelled
      const postRef = doc(db, "posts", postId).withConverter(postConverter);
      await updateDoc(postRef, {
        [`connections.${connectionId}.error.cancelled`]: true,
        updatedAt: Date.now(),
      });

      toast({
        title: "Retry cancelled",
        description: "The retry has been cancelled successfully.",
      });
    } catch (error) {
      console.error("Error cancelling retry:", error);
      toast({
        title: "Error cancelling retry",
        description:
          error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setCancelingRetry(null);
    }
  };

  const saveFeedback = async ({
    rating = feedbackSelection,
    comment = feedbackComment,
    showSuccessToast = true,
    closeInput = true,
    syncCommentState = true,
  }: {
    rating?: "up" | "down" | null;
    comment?: string;
    showSuccessToast?: boolean;
    closeInput?: boolean;
    syncCommentState?: boolean;
  } = {}) => {
    if (!activeWorkspaceId || !currentUser || !currentPostData?.id || !rating) {
      if (showSuccessToast) {
        toast({
          title: "Cannot submit feedback",
          description: "Please select a rating (thumbs up or down).",
          variant: "destructive",
        });
      }
      return;
    }

    setIsSubmittingFeedback(true);
    try {
      const trimmedComment = comment.trim();
      const feedbackPayload: Omit<PostFeedback, "id"> = {
        postId: currentPostData.id,
        workspaceId: activeWorkspaceId,
        userId: currentUser.uid,
        rating,
        comment: trimmedComment,
        timestamp: Date.now(),
      };

      if (existingFeedbackId) {
        const feedbackDocRef = doc(db, "post_feedbacks", existingFeedbackId);
        await updateDoc(feedbackDocRef, feedbackPayload); // Firestore merges by default, this replaces fields
        if (showSuccessToast) {
          toast({
            title: "Feedback updated",
            description: "Your feedback has been successfully updated.",
          });
        }
      } else {
        const feedbackCollectionRef = collection(db, "post_feedbacks");
        const newFeedbackDocRef = await addDoc(
          feedbackCollectionRef,
          feedbackPayload
        );
        setExistingFeedbackId(newFeedbackDocRef.id);
        if (showSuccessToast) {
          toast({
            title: "Feedback submitted",
            description: "Thank you for your feedback!",
          });
        }
      }
      setFeedbackSelection(rating);
      if (syncCommentState) {
        setFeedbackComment(trimmedComment);
      }
      setInitialFeedbackRating(rating);
      setInitialFeedbackComment(trimmedComment);
      if (closeInput) {
        setShowFeedbackInput(false);
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast({
        title: "Error submitting feedback",
        description:
          error instanceof Error
            ? error.message
            : "Could not save your feedback.",
        variant: "destructive",
      });
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const handleFeedbackSubmit = () => {
    void saveFeedback({ closeInput: true, showSuccessToast: true });
  };

  const handleFeedbackSelection = (rating: "up" | "down") => {
    if (isSubmittingFeedback) {
      return;
    }
    setFeedbackSelection(rating);
    setShowFeedbackInput(true);
    void saveFeedback({
      rating,
      comment: feedbackComment,
      closeInput: false,
      showSuccessToast: false,
      syncCommentState: false,
    });
  };

  const handleFeedbackCommentBlur = (
    event: FocusEvent<HTMLTextAreaElement>
  ) => {
    const nextFocusedElement = event.relatedTarget as HTMLElement | null;
    if (nextFocusedElement?.closest("[data-feedback-action]")) {
      return;
    }

    if (
      !isSubmittingFeedback &&
      feedbackSelection &&
      (feedbackSelection !== initialFeedbackRating ||
        feedbackComment.trim() !== initialFeedbackComment)
    ) {
      void saveFeedback({ closeInput: false, showSuccessToast: false });
    }
  };

  const handleDiscardFeedback = () => {
    setShowFeedbackInput(false);
    setFeedbackSelection(initialFeedbackRating);
    setFeedbackComment(initialFeedbackComment);
  };

  const handleClose = () => {
    // Reset all media-related state
    setCurrentVideoTime(0);
    setIsDetailsSheetExpandedOnMobile(false);
    onClose();
  };

  // Reset state when component unmounts or closes
  useEffect(() => {
    if (!isOpen) {
      setCurrentVideoTime(0);
    }
    return () => {
      setCurrentVideoTime(0);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  if (listenerError) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm cursor-pointer"
          onClick={handleClose}
          role="presentation"
        />
        <Card className="p-6 pointer-events-auto text-center bg-background">
          <h3 className="text-lg font-semibold text-red-600 mb-2">
            Error Loading Post
          </h3>
          <p className="text-sm text-gray-600 mb-4">{listenerError.message}</p>
          <Button onClick={handleClose} variant="outline">
            Close
          </Button>
        </Card>
      </div>
    );
  }

  if (!currentPostData) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm"
          role="presentation"
        />
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  const hasMediaItems = currentPostData.mediaItems && currentPostData.mediaItems.length > 0;

  return (
    <div className="fixed inset-0 z-50 bg-black md:bg-transparent">
      {/* Backdrop with blur effect */}
      <div
        className="fixed inset-0 bg-black/80 backdrop-blur-sm cursor-pointer"
        onClick={handleClose}
        role="presentation"
      />

      {/* Content */}
      <div className="fixed inset-0 flex flex-col justify-start md:justify-center pointer-events-none">
        {canPreviewFormats && (
          <div className="flex h-16 shrink-0 items-center justify-center">
            <div className="pointer-events-auto rounded-xl border border-border/60 bg-background p-1 shadow-lg">
              <PostFormatToggle label="Preview" hideLabel mediaCount={currentPostData.mediaItems.length}
                video={showVideoMode} onChange={handlePreviewFormatChange} loading={isPreparingVideo} />
            </div>
          </div>
        )}
        {/* Main container: on mobile, full-screen flex column; on desktop, centered flex row. */}
        <Card className="bg-black md:bg-background pointer-events-auto flex flex-col md:flex-row flex-1 min-h-0 md:flex-none md:h-auto md:max-h-screen w-full overflow-hidden md:overflow-y-auto rounded-none md:rounded-lg border-none md:border md:w-auto md:mx-auto">
          <div className="flex shrink-0 flex-col" style={{ width: dimensions.width }}>
          {/* Left side - Media display */}
          <div
            className="bg-black flex items-center justify-center relative md:w-auto shrink-0"
            style={{
              width: `${dimensions.width}px`,
              height:
                window.innerWidth < 768 && isDetailsSheetExpandedOnMobile
                  ? `${Math.round(window.innerHeight * 0.4)}px`
                  : `${dimensions.height}px`,
            }}
          >
            <div className="relative" style={{ width: "100%", height: "100%" }}>
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  position: "relative",
                }}
              >
                {showVideoMode && speedAdjustedChoreography && resolvedVideoSlides.length > 0 ? (
                  <CarouselVideoPreview
                    slides={resolvedVideoSlides}
                    choreography={speedAdjustedChoreography}
                    containerWidth={resolvedVideoSlides[0]?.dimensions.width ?? 1080}
                    containerHeight={resolvedVideoSlides[0]?.dimensions.height ?? 1350}
                    layoutEngine="remotion"
                    autoPlay
                    startOnCover
                    musicUrl={currentPostData.videoAudioMuted ? undefined : videoMusicUrl}
                    style={{ width: '100%', height: '100%', backgroundColor: 'transparent' }}
                  />
                ) : hasMediaItems ? (
                  currentPostData.mediaItems.map((item, idx) =>
                    item ? (
                      <div
                        key={`${item.path}-${idx}`}
                        style={{
                          position: "absolute",
                          inset: 0,
                          visibility:
                            idx === currentIndex ? "visible" : "hidden",
                        }}
                      >
                        <MediaPlayer
                          mediaItem={item}
                          isControlsVisible={idx === currentIndex}
                          autoPlay={false}
                          isFreePlan={isFreePlan}
                          mediaUrls={mergedMediaUrls}
                          borderless
                        />
                      </div>
                    ) : null
                  )
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50">
                    <ImageIcon className="h-12 w-12 text-gray-300 mb-2" />
                    <p className="text-gray-400 text-sm font-medium">No media</p>
                  </div>
                )}
              </div>

              {/* Mobile close button - top right over media */}
              <Button
                variant="ghost"
                size={iconButtonSize}
                onClick={handleClose}
                className="md:hidden absolute top-4 right-4 text-white bg-black/50 hover:bg-black/70 backdrop-blur-sm z-20 rounded-full w-10 h-10 p-0 flex items-center justify-center"
              >
                <X className="h-5 w-5" />
              </Button>

              {/* Media counter */}
              {currentPostData.mediaItems.length > 1 && !showVideoMode && (
                <div className="absolute top-4 left-4 bg-black/50 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-sm z-20">
                  {" "}
                  {/* Increased z-index */}
                  {currentIndex + 1} / {currentPostData.mediaItems.length}
                </div>
              )}

              {/* Navigation arrows */}
              {currentPostData.mediaItems.length > 1 && !showVideoMode && (
                <>
                  <Button
                    variant="ghost"
                    size={iconButtonSize}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-white bg-black/70 hover:bg-black/80 backdrop-blur-md shadow-lg z-20 rounded-full w-10 h-10 p-0 flex items-center justify-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentIndex((prev) =>
                        prev === 0
                          ? currentPostData.mediaItems.length - 1
                          : prev - 1
                      );
                    }}
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </Button>
                  <Button
                    variant="ghost"
                    size={iconButtonSize}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white bg-black/70 hover:bg-black/80 backdrop-blur-md shadow-lg z-20 rounded-full w-10 h-10 p-0 flex items-center justify-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentIndex((prev) =>
                        prev === currentPostData.mediaItems.length - 1
                          ? 0
                          : prev + 1
                      );
                    }}
                  >
                    <ChevronRight className="h-6 w-6" />
                  </Button>
                </>
              )}
            </div>
          </div>


          </div>

          {/* Right side - Post details (Sheet) */}
          <div
            className="
              bg-background w-full
              flex-1 min-h-0 overflow-y-auto
              md:relative md:w-96 md:border-l md:border-t-0 md:h-auto md:flex-none md:flex-auto
              border-t
            "
            style={{
              height:
                window.innerWidth >= 768
                  ? `${dimensions.height}px`
                  : undefined, // flex-1 fills remaining space on mobile
            }}
          >
            {/* Inner container for padding and layout */}
            <div className="p-4 md:p-6 h-full flex flex-col">
              {/* Swipe area: drag handle + header */}
              <div ref={swipeAreaRef} className="flex-shrink-0">
                {/* Drag handle for mobile swipe */}
                <div className="md:hidden flex justify-center pb-2 -mt-1">
                  <div className="w-10 h-1 rounded-full bg-gray-300" />
                </div>
                {/* Header */}
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-semibold text-lg">Post Details</h3>
                  {isLocalhost() && (
                    <p className="text-xs text-gray-500 mt-1">
                      ID:{" "}
                      <span className="font-mono">{currentPostData.id}</span>
                    </p>
                  )}
                </div>
                <div className="flex gap-1 items-center">
                  <Button
                    variant="ghost"
                    size={iconButtonSize}
                    onClick={() =>
                      setIsDetailsSheetExpandedOnMobile(
                        !isDetailsSheetExpandedOnMobile
                      )
                    }
                    className="block md:hidden mr-1 p-2"
                  >
                    {isDetailsSheetExpandedOnMobile ? (
                      <ChevronDown className="h-5 w-5" />
                    ) : (
                      <ChevronUp className="h-5 w-5" />
                    )}
                  </Button>
                  {/* Overflow menu for duplicate/delete */}
                  {!readOnly && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size={iconButtonSize}
                          className="p-2"
                        >
                          <MoreVertical className="h-5 w-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        {isEditable && (
                          <>
                            <DropdownMenuItem
                              onClick={() => handleEditAndClose(currentPostData.id)}
                            >
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            {canPublish && (
                              <DropdownMenuItem
                                onClick={() =>
                                  handleEditAndClose(currentPostData.id, { intent: "publish", from: "post_viewer" })
                                }
                              >
                                <Send className="h-4 w-4 mr-2" />
                                Publish
                              </DropdownMenuItem>
                            )}
                          </>
                        )}
                        <DropdownMenuItem
                          onClick={() => {
                            handleDuplicate(currentPostData.id);
                            onClose();
                          }}
                        >
                          <Copy className="h-4 w-4 mr-2" />
                          Duplicate
                        </DropdownMenuItem>
                        {getDeleteDialog(
                          currentPostData,
                          hasPublished,
                          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                            <Trash2 className="h-4 w-4 mr-2 text-red-600" />
                            <span className="text-red-600">Delete</span>
                          </DropdownMenuItem>,
                          () => {
                            onClose();
                          }
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}

                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size={iconButtonSize}
                          onClick={() => {
                            handleClose();
                          }}
                          className="hidden md:inline-flex hover:bg-gray-100 ml-2"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Close</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
              </div>

              {/* Scrollable Content Area (content always rendered, parent div clips/scrolls) */}
              <div className="mt-4 flex-grow space-y-4 md:space-y-6 overflow-y-auto">
                <Separator />

                {/* Watermark Upgrade Banner */}
                {isFreePlan && currentItem?.metadata?.generatedWithWatermark && (
                  <Alert className="bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200 dark:from-orange-950/20 dark:to-amber-950/20 dark:border-orange-800">
                    <Sparkles className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                    <AlertDescription className="flex items-center justify-between gap-3">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Upgrade to remove watermark
                      </p>
                      <Button
                        size="sm"
                        onClick={() => navigateToUpgrade({ reason: "watermark" })}
                        className="bg-orange-600 hover:bg-orange-700 text-white shrink-0"
                      >
                        Upgrade Now
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}

                {/* Title Section */}
                {currentPostData.title && (
                  <div>
                    <h4 className="font-medium text-sm text-gray-500 mb-2">
                      Title
                    </h4>
                    <p
                      className="notranslate text-sm text-gray-700 break-words"
                      translate="no"
                    >
                      {currentPostData.title}
                    </p>
                  </div>
                )}

                {/* Description Section */}
                {currentPostData.description && (
                  <div>
                    <h4 className="font-medium text-sm text-gray-500 mb-2">
                      Description
                    </h4>
                    <p
                      className="notranslate text-sm text-gray-700 whitespace-pre-line"
                      translate="no"
                    >
                      {currentPostData.description}
                    </p>
                  </div>
                )}

                {/* Feedback Section */}
                {currentPostData.generationSource && !readOnly && (
                  <div>
                    <h4 className="font-medium text-sm text-gray-500 mb-2">
                      Feedback on Generation
                    </h4>
                    <div className="flex space-x-2 mb-2">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size={iconButtonSize}
                              aria-label="Thumbs up"
                              className={`p-2 rounded-md transition-colors 
                              ${
                                feedbackSelection === "up"
                                  ? "bg-green-100 hover:bg-green-200 dark:bg-green-800 dark:hover:bg-green-700" // Selected Up styles
                                  : "hover:bg-gray-200 dark:hover:bg-gray-700" // Unselected hover
                              }
                              ${existingFeedbackId && feedbackSelection === "up" && feedbackComment ? "cursor-default" : ""}
                            `}
                              onClick={() => handleFeedbackSelection("up")}
                              disabled={isSubmittingFeedback}
                            >
                              <ThumbsUp
                                className={`h-5 w-5 ${feedbackSelection === "up" ? "text-green-700 dark:text-green-300" : "text-green-500 dark:text-green-400"}`}
                              />
                            </Button>
                          </TooltipTrigger>
                          {existingFeedbackId &&
                            feedbackSelection === "up" &&
                            feedbackComment && (
                              <TooltipContent>
                                <p className="text-xs max-w-xs break-words whitespace-pre-line">
                                  {feedbackComment}
                                </p>
                              </TooltipContent>
                            )}
                        </Tooltip>
                      </TooltipProvider>

                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size={iconButtonSize}
                              aria-label="Thumbs down"
                              className={`p-2 rounded-md transition-colors 
                              ${
                                feedbackSelection === "down"
                                  ? "bg-red-100 hover:bg-red-200 dark:bg-red-800 dark:hover:bg-red-700" // Selected Down styles
                                  : "hover:bg-gray-200 dark:hover:bg-gray-700" // Unselected hover
                              }
                              ${existingFeedbackId && feedbackSelection === "down" && feedbackComment ? "cursor-default" : ""}
                            `}
                              onClick={() => handleFeedbackSelection("down")}
                              disabled={isSubmittingFeedback}
                            >
                              <ThumbsDown
                                className={`h-5 w-5 ${feedbackSelection === "down" ? "text-red-700 dark:text-red-300" : "text-red-500 dark:text-red-400"}`}
                              />
                            </Button>
                          </TooltipTrigger>
                          {existingFeedbackId &&
                            feedbackSelection === "down" &&
                            feedbackComment && (
                              <TooltipContent>
                                <p className="text-xs max-w-xs break-words whitespace-pre-line">
                                  {feedbackComment}
                                </p>
                              </TooltipContent>
                            )}
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    {showFeedbackInput && (
                      <Textarea
                        placeholder="Optional: Tell us more..."
                        value={feedbackComment}
                        onChange={(e) => setFeedbackComment(e.target.value)}
                        onBlur={handleFeedbackCommentBlur}
                        className="text-sm"
                      />
                    )}
                    {showFeedbackInput && (
                      <div className="mt-3 flex space-x-2">
                        <Button
                          onClick={handleFeedbackSubmit}
                          data-feedback-action
                          disabled={!feedbackSelection || isSubmittingFeedback}
                          className="flex-1"
                          size="sm"
                        >
                          {isSubmittingFeedback
                            ? "Submitting..."
                            : existingFeedbackId
                              ? "Update Feedback"
                              : "Submit Feedback"}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={handleDiscardFeedback}
                          data-feedback-action
                          disabled={isSubmittingFeedback}
                          className="flex-1"
                          size="sm"
                        >
                          Discard
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Source Section */}
                {currentPostData.generationSource && (
                  <div>
                    <h4 className="font-medium text-sm text-gray-500 mb-2">
                      Source
                    </h4>
                    <PostSource
                      source={currentPostData.generationSource}
                      className="text-sm break-all"
                    />
                  </div>
                )}

                {/* Connections */}
                {currentPostData.connections &&
                  Object.keys(currentPostData.connections).length > 0 && (
                    <div>
                      <h4 className="font-medium text-sm text-gray-500 mb-3">
                        Publish Accounts
                      </h4>
                      <div className="space-y-3">
                        {Object.entries(currentPostData.connections || {}).map(
                          ([connectionId, connection]) => {
                            const platformConnection =
                              connectionsById[connectionId];

                            // Skip if account not found AND not published (published should always show)
                            if (!platformConnection && connection.status !== "published") {
                              return null;
                            }

                            // Use platform from connection data as fallback for disconnected accounts
                            const platform = platformConnection?.platform || connection.platform;
                            const profileName = platformConnection?.profileName || "(Disconnected)";
                            const profileImage = platformConnection?.profileImage;

                            return (
                              <div
                                key={connectionId}
                                className="flex items-center justify-between bg-gray-50 rounded-lg p-3"
                              >
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <PlatformConnectionIcon
                                    platform={platform}
                                    profileImage={profileImage}
                                    profileName={profileName}
                                    size="md"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">
                                      {profileName}
                                    </p>
                                    <div className="flex flex-col gap-0.5">
                                      <p className="text-xs font-medium text-gray-600">
                                        {platform}
                                        {analyticsUiEnabled &&
                                          platformConnection?.audience
                                            ?.followerCount != null &&
                                          ` · ${platformConnection.audience.followerCount.toLocaleString()} followers`}
                                      </p>
                                      <p className="text-[11px] text-gray-400">
                                        {connection.status === "pending" &&
                                        (connection.scheduledFor || currentPostData.scheduledFor)
                                          ? `Scheduled for ${formatDate(connection.scheduledFor ?? currentPostData.scheduledFor)}`
                                          : connection.status ===
                                              "pre-processing"
                                            ? `Processing media...`
                                            : connection.status ===
                                                  "published" &&
                                                connection.publishedAt
                                              ? `Published ${formatDate(connection.publishedAt)}`
                                              : null}
                                      </p>
                                      {analyticsUiEnabled &&
                                        connection.status === "published" && (
                                          <PostMetricsRow
                                            analytics={
                                              analyticsByConnectionId[
                                                connectionId
                                              ]
                                            }
                                            reconnectHint={analyticsReconnectNeeded(
                                              platformConnection
                                            )}
                                            connectionUpdatedAt={
                                              platformConnection?.updatedAt
                                            }
                                          />
                                        )}
                                      {connection.status === "failed" &&
                                        connection.error && platformConnection && !readOnly && (
                                          <ErrorActionButtons
                                            postId={currentPostData.id}
                                            connectionId={connectionId}
                                            platform={platform}
                                            errorCode={connection.error.code || "UNKNOWN"}
                                            errorMessage={connection.error.message}
                                            errorTimestamp={connection.error.timestamp}
                                            retryCount={connection.error.retryCount || 0}
                                            retryable={connection.error.retryable}
                                            cancelled={connection.error.cancelled}
                                            hasPublishedConnections={hasPublished}
                                            accountReconnected={
                                              platformConnection.status !== "expired" &&
                                              platformConnection.status !== "revoked"
                                            }
                                            storiesAlreadyLive={
                                              connection.storyProgress && {
                                                live: connection.storyProgress.storyIds.length,
                                                total: connection.storyProgress.total,
                                              }
                                            }
                                            onCancelRetry={handleCancelRetry}
                                            onEditPost={(postId) =>
                                              handleEditAndClose(postId, { openEditor: true })
                                            }
                                          />
                                        )}
                                      {connection.firstCommentResult?.status === "failed" && (
                                        <p className="text-[11px] text-yellow-600">
                                          First comment could not be posted. You can add it manually.
                                        </p>
                                      )}
                                      {connection.processedMedia?.type ===
                                        "video" &&
                                        connection.processedMedia
                                          ?.processedPath && (
                                          <ProcessedMediaDownloader
                                            path={
                                              connection.processedMedia
                                                .processedPath
                                            }
                                          />
                                        )}
                                    </div>
                                  </div>
                                </div>
                                <div className="ml-3 flex-shrink-0">
                                  <StatusBadge status={connection.status} />
                                </div>
                              </div>
                            );
                          }
                        )}
                      </div>
                    </div>
                  )}
              </div>
              {/* Action Footer */}
              {isEditable && !readOnly && (
                <div className="mt-auto flex-shrink-0 pt-4">
                  {/* Warning when editing a post with published connections */}
                  {hasPublished && (
                    <Alert className="mb-3 bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-800">
                      <AlertDescription className="text-sm text-amber-800 dark:text-amber-200">
                        Some platforms have already published. Edits will only apply to pending connections.
                      </AlertDescription>
                    </Alert>
                  )}
                  <div className={canPublish ? "grid grid-cols-2 gap-2" : "grid"}>
                    <Button
                      onClick={() => handleEditAndClose(currentPostData.id)}
                      size="lg"
                      variant="outline"
                    >
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit
                    </Button>
                    {canPublish && <Button
                      onClick={() =>
                        handleEditAndClose(currentPostData.id, { intent: "publish", from: "post_viewer" })
                      }
                      size="lg"
                      variant="brand"
                    >
                      <Send className="mr-2 h-4 w-4" />
                      Publish
                    </Button>}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>

    </div>
  );
}
